import { PropsWithChildren, useEffect } from 'react';
import { useMantineTheme } from '@mantine/core';

/** Migration Mantine v5 - v7
 * Les variables Mantine n'existent pas dans v5.
 * On les rajoute temporairement ici: createMantineVariables().
 * Après la migration pour v7:
 * - Juste supprimer la fonction createMantineVariables()
 * - Rien à modifier dans les autres fichiers
 * Liste des variables Mantine v7: https://v7.mantine.dev/styles/css-variables-list
 */

function createMantineVariables(theme: ReturnType<typeof useMantineTheme>) {
  const vars: Record<string, string> = {};
  const COLORS = ['gray', 'dark', 'blue', 'green', 'yellow', 'orange', 'grape', 'pink'] as const;
  type Palette = (typeof COLORS)[number];

  // colors
  const addPalette = (name: Palette) => {
    theme.colors[name].forEach((shade, index) => {
      vars[`--mantine-color-${name}-${index}`] = shade;
    });
  };
  COLORS.forEach(addPalette);

  // spacing
  Object.entries(theme.spacing).forEach(([key, value]) => {
    vars[`--mantine-spacing-${key}`] = `${value}px`;
  });

  // radius
  Object.entries(theme.radius).forEach(([key, value]) => {
    vars[`--mantine-radius-${key}`] = `${value}px`;
  });

  // shadow
  Object.entries(theme.shadows).forEach(([key, value]) => {
    vars[`--mantine-shadow-${key}`] = `${value}`;
  });

  return vars;
}

export function AppStyleProvider({ children }: PropsWithChildren) {
  const theme = useMantineTheme();

  useEffect(() => {
    const root = document.documentElement;
    const isDark = theme.colorScheme === 'dark';

    const appVars: Record<string, string> = {
      '--app-bg-light': theme.colors.gray[0],
      '--app-bg-dark': theme.colors.dark[8],
      '--app-bg': isDark ? theme.colors.dark[8] : theme.colors.gray[0],
      '--app-bg-hover': isDark ? theme.colors.dark[5] : theme.colors.gray[2],

      '--app-color-blue': theme.other.colors.appBlue,
      '--app-color-blue-dark': theme.other.colors.appDarkBlue,
      '--app-color-blue-highlight': theme.other.colors.highLightBlue,

      '--accordion-bg': isDark ? theme.colors.dark[7] : 'white',

      '--icon-color': isDark ? theme.colors.blue[7] : theme.colors.blue[6],

      '--link-color': isDark ? theme.colors.blue[5] : theme.colors.blue[9],
      '--link-hover': isDark ? theme.colors.blue[3] : theme.colors.blue[7],

      '--table-border': isDark ? theme.colors.dark[4] : theme.colors.gray[3],
      '--table-row-odd': isDark ? theme.colors.dark[5] : theme.other.colors.highLightBlue,
      '--table-row-even': isDark ? theme.colors.dark[6] : theme.colors.gray[0],
      '--table-row-hover': isDark ? theme.colors.dark[7] : theme.colors.gray[2],
      '--table-row-selected': isDark ? theme.colors.grape[9] : theme.colors.grape[1],

      '--text-color': isDark ? 'white' : 'black',

      '--title-color': isDark ? theme.colors.gray[4] : theme.other.colors.appBlue,
    };

    const mantineVars = createMantineVariables(theme);

    const vars = {
      ...appVars,
      ...mantineVars,
    };

    Object.entries(vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, [theme]);

  return <div>{children}</div>;
}
